import {useCallback, useMemo, useRef, useState} from 'react';
import {XHR_FAILED_STATE, XHR_LOADING_STATE} from '../../../assets/constants/constants';
import type {TXHR} from '../../../assets/constants/constansts.type';
import {logGoogleEvent} from '../../../utils/analytics';
import type {AssistantDraftPayload, ChatMessage, DraftResult, PendingChatAttachment, UserChatMessage} from './types';
import {buildUserMessage, canSubmitPrompt, revokeMessageAttachmentUrls} from './attachmentHelpers';
import {createMessageId} from './createMessageId';

interface UseAIMessageGeneratorArgs {
    errorMessage: string;
    submit: {
        label: string;
        onClick: (prompt: string, history: ChatMessage[], sessionId: string, attachment?: PendingChatAttachment | null) => Promise<DraftResult | null>;
        xhrState?: TXHR;
    };
    approve?: {
        label: string;
        onClick: (draft: AssistantDraftPayload) => Promise<void>;
        xhrState?: TXHR;
    };
    onClose?: () => void;
    onReset?: () => void;
    analytics?: {
        contentId: string | number;
        sourceTypeId: string;
        context?: Record<string, unknown>;
    };
}

export function useAIMessageGenerator({
    errorMessage,
    submit,
    approve,
    onClose = () => {},
    onReset,
    analytics,
}: UseAIMessageGeneratorArgs) {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [promptText, setPromptText] = useState('');
    const [resetKey, setResetKey] = useState(0);
    // A handler that resolves to null produced no answer — surface it like a failed request.
    const [emptyResultError, setEmptyResultError] = useState(false);
    const sessionIdRef = useRef(createMessageId());

    const draftLoading = submit.xhrState === XHR_LOADING_STATE;
    const approveLoading = approve?.xhrState === XHR_LOADING_STATE;

    const error = useMemo(() => {
        if(submit.xhrState === XHR_FAILED_STATE || approve?.xhrState === XHR_FAILED_STATE || emptyResultError) {
            return errorMessage;
        }
        return null;
    }, [submit.xhrState, approve?.xhrState, emptyResultError, errorMessage]);

    const reset = useCallback(() => {
        revokeMessageAttachmentUrls(messages);
        setMessages([]);
        setPromptText('');
        setEmptyResultError(false);
        setResetKey(key => key + 1);
        sessionIdRef.current = createMessageId();
        onReset?.();
    }, [messages, onReset]);

    const submitPrompt = useCallback(async (
        promptOverride?: string,
        pendingAttachment?: PendingChatAttachment | null,
        displayText?: string,
    ) => {
        const trimmed = (promptOverride ?? promptText).trim();
        if(!canSubmitPrompt(trimmed, pendingAttachment) || draftLoading) {
            return;
        }
        const priorMessages = messages;
        const visibleText = (displayText ?? trimmed).trim() || trimmed;
        const userMessage = buildUserMessage(visibleText, pendingAttachment);
        setMessages(prev => [...prev, userMessage]);
        setPromptText('');
        setEmptyResultError(false);
        try {
            const result = await submit.onClick(trimmed, priorMessages, sessionIdRef.current, pendingAttachment);
            if(result === null) {
                setEmptyResultError(true);
                return;
            }
            setMessages(prev => [...prev, {
                role: 'assistant',
                text: result.text || '',
                parameterValues: result.parameterValues || undefined,
                id: createMessageId(),
            }]);
        } catch {
            revokeMessageAttachmentUrls([userMessage]);
            setMessages(prev => prev.filter(message => message !== userMessage));
            setPromptText(trimmed);
        }
    }, [promptText, draftLoading, messages, submit.onClick]);

    const editUserMessage = useCallback(async (messageIndex: number, newText: string) => {
        const trimmed = newText.trim();
        if(!trimmed || draftLoading) {
            return;
        }
        const targetMessage = messages[messageIndex];
        if(targetMessage?.role !== 'user') {
            return;
        }
        const priorMessages = messages.slice(0, messageIndex);
        const previousMessages = messages;
        const updatedUserMessage: UserChatMessage = {
            ...targetMessage,
            text: trimmed,
        };
        // Everything after the edited message is dropped, but its preview URLs are only
        // released once the edit sticks — a failure restores those messages.
        const droppedMessages = messages.slice(messageIndex + 1);
        setMessages([...priorMessages, updatedUserMessage]);
        setEmptyResultError(false);
        try {
            const result = await submit.onClick(trimmed, priorMessages, sessionIdRef.current, null);
            revokeMessageAttachmentUrls(droppedMessages);
            if(result === null) {
                setEmptyResultError(true);
                return;
            }
            setMessages(prev => [...prev, {
                role: 'assistant',
                text: result.text || '',
                parameterValues: result.parameterValues || undefined,
                id: createMessageId(),
            }]);
        } catch {
            setMessages(previousMessages);
        }
    }, [draftLoading, messages, submit.onClick]);

    const handleApprove = useCallback(async (draft: AssistantDraftPayload) => {
        if(!approve || approveLoading) {
            return;
        }
        if(analytics) {
            const messageIndex = messages.findIndex(message => message === draft);
            const assistantIndex = messageIndex >= 0
                ? messages.slice(0, messageIndex).filter(message => message.role === 'assistant').length
                : messages.filter(message => message.role === 'assistant').length - 1;
            logGoogleEvent('ai_message_generator_approve_click', analytics.contentId, analytics.sourceTypeId, {
                popup_name: 'aiMessageGenerator',
                ...(analytics.context ?? {}),
                button_name: 'approve',
                assistant_index: assistantIndex,
            });
        }
        await approve.onClick(draft);
        reset();
        onClose();
    }, [approveLoading, approve, onClose, analytics, messages, reset]);

    return {
        messages,
        promptText,
        setPromptText,
        draftLoading,
        approveLoading,
        error,
        submitPrompt,
        editUserMessage,
        handleApprove,
        reset,
        resetKey,
    };
}
